// German (du) copy for /web-uploader, translated from copy.en.ts. Same shape (checked by copy.test.ts).
// "Der Web-Uploader" as in the site's footer (src/i18n/locales/de.json); "Vorschaubilder" and "Originale".
// The uploader's own labels are in English, so they're quoted as it shows them („Previews“, „Originals“).
// The text inside the two animations stays in English, like the screenshots (the uploader is in English);
// their captions, legends and accessible names are translated.
import type { WebUploaderCopy } from "./copy.en";
import en from "./copy.en";

const de: WebUploaderCopy = {
  seo: {
    title: "Der Web-Uploader von Photohawk | Schneller als FTP",
    description:
      "Wir haben den Web-Uploader von Photohawk neu gebaut. Er lädt viele Fotos gleichzeitig hoch, schafft Uploads mit mehr als 100.000 Fotos und macht von selbst weiter, wenn die Verbindung abbricht – in allen Plänen. In allen Plänen gehen zuerst leichte Vorschaubilder hoch, damit deine Galerie in Minuten online ist, nicht erst nach Stunden.",
  },
  hero: {
    badge: "Neu",
    eyebrow: "In allen Plänen",
    title: "Der neue Web-Uploader von Photohawk",
    subtitle:
      "Schneller als FTP, gebaut für Uploads mit mehr als 100.000 Fotos, und er macht genau dort weiter, wo er aufgehört hat.",
    plans: "In allen Plänen ist ein großes Event dank „Vorschaubilder zuerst“ in Minuten online, nicht erst nach Stunden.",
    ctaPrimary: "Web-Uploader öffnen",
    ctaSecondary: "Das ist neu",
    screenshotAlt:
      "Der Web-Uploader mitten im Upload. Mit Vorschaubildern zuerst (in allen Plänen) zeigt die Statusleiste, dass die Galerie mit Vorschaubildern online ist und wie viele Personen sie gerade besuchen, und die Warteschlange rechts zeigt die fertigen Vorschaubilder und die Originale, die noch unterwegs sind, wobei zwei gekaufte Fotos zuerst dran sind.",
    phoneAlt: "Der Web-Uploader auf einem Smartphone, geöffnet in einem Ordner der Galerie, mit dem Upload-Status unten.",
  },
  previews: {
    heading: "Vorschaubilder zuerst",
    badge: "In allen Plänen",
    subtitle:
      "In allen Plänen: in Minuten online, nicht erst nach Stunden. Eine Galerie mit 10.000 Fotos kann bei einem Upload mit 20 Mbps in etwa 8 Minuten online sein und verkaufen.",
    body: "Leichte Vorschaubilder gehen zuerst hoch. So kann deine Galerie schon online gehen und verkaufen oder kostenlose Downloads anbieten, während die Originale nachkommen. Kauft oder lädt jemand ein Foto herunter, bevor sein Original da ist, rückt dieses Original in der Warteschlange nach vorn.",
    steps: [
      { id: "previews", text: "Leichte Vorschaubilder gehen zuerst hoch" },
      { id: "live", text: "Deine Galerie geht online" },
      { id: "buy", text: "Jemand kauft ein Foto" },
      { id: "jump", text: "Sein Original rückt nach vorn" },
      { id: "originals", text: "Die Originale kommen nach" },
    ],
    anim: {
      ...en.previews.anim,
      label: "Animierte Darstellung: Vorschaubilder zuerst",
      description:
        "Leichte Vorschaubilder von sechs Fotos fliegen von deinem Computer in deine Galerie, und die Galerie schaltet auf „Live“. Jemand tippt bei einem Foto auf „Buy“: Sein Original rückt an die Spitze der Warteschlange für die Originale und geht als Erstes hoch. Die übrigen Originale folgen.",
    },
    link: "So funktionieren Uploads mit Vorschau zuerst",
  },
  faster: {
    heading: "Warum er schneller ist als FTP",
    subtitle: "Dieselben Fotos, auf zwei Arten verschickt.",
    anim: {
      ...en.faster.anim,
      label: "Animierter Vergleich: ein FTP-Programm und der Web-Uploader",
      description:
        "Zwei Reihen. Oben schickt ein FTP-Programm die Fotos standardmäßig 2 gleichzeitig und höchstens 10. Unten schickt der Web-Uploader viele Fotos gleichzeitig, auf Spuren, die je nach Verbindung breiter und schmaler werden. In allen Plänen gehen leichte Vorschaubilder zuerst, deshalb ist seine Galerie früher online. Die Verbindung bricht kurz ab, und der Upload macht von selbst weiter.",
      legendPreviews: "Vorschaubilder",
      legendOriginals: "Originale",
    },
    points: [
      {
        id: "parallel",
        title: "Viele Fotos gleichzeitig",
        body: "Er lädt viele Fotos gleichzeitig hoch, weit mehr als ein FTP-Programm wie FileZilla (standardmäßig 2 gleichzeitig und höchstens 10).",
      },
      {
        id: "adaptive",
        title: "Nutzt deine Verbindung voll aus",
        body: "Er passt sich beim Hochladen laufend an, um deine Verbindung voll auszunutzen.",
      },
      {
        id: "scale",
        title: "Gebaut für große Events",
        body: "Wir haben ihn im großen Maßstab getestet: Uploads mit mehr als 100.000 Fotos schafft er mühelos.",
      },
      {
        id: "live",
        title: "In Minuten online, nicht erst nach Stunden (in allen Plänen)",
        body: "In allen Plänen gehen zuerst leichte Vorschaubilder hoch. So kann eine Galerie mit 10.000 Fotos bei einem Upload mit 20 Mbps in etwa 8 Minuten online sein und verkaufen, während die Originale nachkommen.",
      },
      {
        id: "resume",
        title: "Macht weiter, wo er aufgehört hat",
        body: "Bricht deine Verbindung ab, macht er von selbst weiter. Füge den Ordner noch einmal hinzu, und nur was fehlt, wird hochgeladen.",
      },
    ],
  },
  /** The upload-time estimator (#estimate). {placeholders} are filled in by the page. */
  estimate: {
    heading: "Wie lange dauert dein Upload?",
    subtitle: "Eine Schätzung für deine Galerie, anhand deiner eigenen Upload-Geschwindigkeit.",
    heroLink: "Upload-Dauer schätzen",
    photos: "Anzahl der Fotos",
    custom: "Andere",
    customLabel: "Anzahl der Fotos (andere)",
    size: "Durchschnittliche Größe eines Originals",
    sizeUnit: "MB",
    sizeHint: "JPEGs von Events haben oft etwa {mb} MB.",
    speed: "Deine Upload-Geschwindigkeit",
    speedUnit: "Mbps",
    test: "Meine Upload-Geschwindigkeit testen",
    testing: "Teste… {mbps} Mbps",
    testStarting: "Teste…",
    testDone: "Gemessen: {mbps} Mbps Upload",
    testFailed: "Wir konnten deine Geschwindigkeit gerade nicht messen. Gib sie selbst ein oder nutze einen allgemeinen Speedtest.",
    dataNote: "Verbraucht bis zu etwa {mb} MB Daten, und nur, wenn du auf die Schaltfläche tippst.",
    otherTest: "Oder einen allgemeinen Speedtest nutzen",
    resultsHeading: "Deine Schätzung",
    previewsLabel: "Vorschaubilder zuerst",
    previewsPlans: "In allen Plänen",
    previewsLive: "Galerie online in {time}",
    previewsAll: "Alle Originale hochgeladen in {time}",
    originalsLabel: "Nur Originale",
    originalsLive: "Galerie online in {time}",
    originalsWhen: "wenn alle {gb} GB Originale oben sind",
    computerNote: "Bei dieser Geschwindigkeit bestimmt dein Computer das Tempo: wie schnell er die Vorschaubilder erstellt.",
    disclaimer: "Nur Schätzungen. Die tatsächliche Dauer hängt von deiner Verbindung, deinem Computer und deinen Fotos ab.",
    time: {
      under: "unter einer Minute",
      min: "etwa {n} Min.",
      h: "etwa {n} Std.",
      d: "etwa {n} Tage",
    },
    table: {
      caption: "Beispiel: 10.000 Fotos",
      speed: "Upload-Geschwindigkeit",
      live: "Vorschaubilder zuerst: Galerie online (in allen Plänen)",
      all: "Alle Originale hochgeladen",
      note: "Der Wert von etwa 8 Minuten wurde bei einem Upload mit 20 Mbps gemessen (Vorschaubilder zuerst). Die anderen Zeilen sind Schätzungen mit demselben Modell, bei Originalen von etwa {mb} MB.",
    },
  },
  features: {
    heading: "Noch mehr Neues",
    subtitle: "Echte Ansichten aus dem neuen Web-Uploader.",
    items: [
      {
        id: "status",
        title: "Der Status in klaren Worten",
        body: "Du siehst auf einen Blick, wo alles steht. Mit Vorschaubildern zuerst (in allen Plänen) heißt das: erst „Previews“ (Vorschaubilder), dann „Originals“ (Originale), jeweils mit eigenem Fortschritt.",
        alt: "Der Upload-Status mit Vorschaubildern zuerst (in allen Plänen): „Live — saving originals…“, eine Fortschrittszeile für „Previews“ und eine für „Originals“ sowie „2 photos people bought go first“.",
      },
      {
        id: "queue",
        title: "Eine Warteschlange, die dahin kommt, wo du sie willst",
        body: "Docke die Upload-Warteschlange rechts, links oder unten an und zieh sie auf die Größe, die dir passt. Dein Browser merkt sich deine Wahl.",
        alt: "Die Upload-Warteschlange unten im Uploader angedockt, mit geöffnetem Menü, um sie nach rechts, unten oder links zu verschieben.",
      },
      {
        id: "visitors",
        title: "Sieh, wer gerade schaut",
        body: "Die Statusleiste zeigt, wie viele Personen deine Galerie gerade ansehen – in allen Plänen.",
        alt: "Die Statusleiste oben im Uploader: „Live on previews“ (mit Vorschaubildern zuerst) und die Zahl der Besucher.",
      },
      {
        id: "resume",
        title: "Macht weiter, wo er aufgehört hat",
        body: "Bricht deine Verbindung ab, macht er von selbst weiter. Füge den Ordner noch einmal hinzu, und nur was fehlt, wird hochgeladen.",
        alt: "Ein Hinweis im Uploader, dass ein Ordner nicht fertig hochgeladen wurde, mit einer Schaltfläche „Resume“.",
      },
      {
        id: "phone",
        title: "Auch auf dem Smartphone übersichtlich",
        body: "Auf kleinen Bildschirmen wird der Ordnerpfad passend eingeklappt. Tippst du auf „…“, siehst du die Ordner dazwischen. So weißt du immer, wohin deine Fotos gehen.",
        alt: "Der Uploader auf einem Smartphone: Der Ordnerpfad zeigt den aktuellen Ordner, und „…“ öffnet eine Liste der Ordner darüber.",
      },
    ],
  },
  ftp: {
    date: "1. November 2026",
    heading: "Was sich am 1. November bei FTP ändert",
    body: "Unsere dauerhaft laufenden gemeinsamen FTP-Server werden am 1. November 2026 abgeschaltet. Du nutzt heute FTP? Dann steig bitte vorher um.",
    points: [
      {
        id: "closes",
        title: "Gemeinsames FTP endet am 1. November 2026",
        body: "Die dauerhaft laufenden gemeinsamen FTP-Server werden am 1. November 2026 abgeschaltet.",
      },
      {
        id: "standard",
        title: "Der Web-Uploader ist der Standardweg zum Hochladen",
        body: "Er ist in allen Plänen enthalten, und du kannst ihn schon heute nutzen.",
      },
      {
        id: "ondemand",
        title: "Wenn FTP unverzichtbar ist",
        body: "In allen Plänen kann jede Person im Web-Uploader ihren eigenen FTP-Server auf Abruf starten, unter einer persönlichen Adresse, die sich nie ändert. Nach einer Stunde ohne Aktivität schaltet er sich ab.",
      },
    ],
    helpLink: "So funktioniert FTP auf Abruf",
  },
  more: {
    previewFirstTitle: "So funktionieren Uploads mit Vorschau zuerst",
    previewFirstBody: "In allen Plänen. Schritt für Schritt: Vorschaubilder, Onlinegehen und was Käufer sehen.",
  },
  cta: {
    heading: "Probier es beim nächsten Upload aus",
    body: "Der neue Web-Uploader ist in allen Plänen bereit. Füge einen Ordner hinzu und sieh zu, wie deine Galerie online geht.",
    button: "Web-Uploader öffnen",
  },
  pause: "Animation anhalten",
  play: "Animation abspielen",
};

export default de;
